import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

import { useChatStore } from '@/store/chatStore';
import { useConfigStore } from '@/store/configStore';
import { translateConfigValue } from '@/utils/translateConfigValue';

import { Button } from './ui/button';

const DEFAULT_WARNING_MESSAGE =
  'Are you still there? This chat will reset if you are inactive for 10 minutes. Let me know if you want to keep the conversation going.';
const DEFAULT_RESET_MESSAGE = 'We reset the chat so you can start fresh.';

const InactivityNotice = () => {
  const {
    showInactivityWarning,
    setShowInactivityWarning,
    showInactivityReset,
    setShowInactivityReset,
  } = useChatStore();
  const { config } = useConfigStore();

  const language = config.language ?? 'de';
  const warningMessage =
    translateConfigValue(config.ui?.inactivity?.warningMessage, language) ??
    DEFAULT_WARNING_MESSAGE;
  const resetMessage =
    translateConfigValue(config.ui?.inactivity?.resetMessage, language) ?? DEFAULT_RESET_MESSAGE;

  const isVisible = showInactivityWarning || showInactivityReset;
  const message = showInactivityWarning ? warningMessage : resetMessage;

  const handleDismiss = () => {
    if (showInactivityWarning) {
      setShowInactivityWarning(false);
    } else {
      setShowInactivityReset(false);
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.2 }}
          className="w-full"
        >
          <div className="flex flex-col gap-3 rounded-xl border border-border bg-white p-4 shadow-widget-window">
            <p className="text-sm text-widget-text-secondary">{message}</p>
            <div className="flex items-center justify-between gap-3">
              {showInactivityWarning ? (
                <Button type="button" variant="outline" size="sm" onClick={handleDismiss}>
                  Keep chatting
                </Button>
              ) : (
                <div className="flex-1" />
              )}
              <button
                type="button"
                onClick={handleDismiss}
                className="rounded-full p-1 text-[#A4A7AE] transition hover:text-widget-text-secondary"
                aria-label="Dismiss inactivity notice"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InactivityNotice;
